<template>
    <div class="search-content__wrapper" @keydown.stop="emitCtrlK" v-focustrap>
        <div class="search-content__wrapper__search-block">
            <IconField class="search-content__icon-field">
                <InputIcon>
                    <i class="pi pi-search search-icon" />
                </InputIcon>
                <InputText v-model.trim="searchValue" type="text" :placeholder="t('global.search')" autofocus
                    @input="debouncedFn" size="large" />
                <InputIcon class="pi pi-times-circle search-content__clear-btn" v-if="searchValue && !dataLoading"
                    @click="clearSearchValue" />
                <InputIcon class="pi pi-spin pi-spinner" v-if="dataLoading" />
            </IconField>
        </div>
        <div class="recent-searches" v-if="!searchValue">
            <span class="recent-searches__title" v-if="!recentSearched.length">{{ t('global.noSearch') }}</span>
            <span class="recent-searches__title" v-else>{{ t('global.recentSearch') }}</span>

            <SearchList v-if="recentSearched.length">
                <SearchListItem v-for="user in recentSearched" :key="user.id" :recent="true" :user="user"
                    :userID="user.id" :userPhoto="user.user_photo" :userFullname="user.full_name"
                    @recentUserClicked="recentUserClicked" />
            </SearchList>
        </div>
        <div class="search-results">
            <span class="search-results__title">{{ searchResultText }}</span>
            <SearchList v-if="searchedUsers.length">
                <SearchListItem v-for="user in searchedUsers" :key="user.id" :recent="false" :user="user"
                    :userID="user.id" :userPhoto="user.user_photo" :userFullname="user.full_name"
                    @userClicked="userClicked" />
            </SearchList>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onBeforeUnmount } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useAuthStore } from '@stores';
import SearchList from './SearchList.vue';
import SearchListItem from './SearchListItem.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n({ useScope: 'global' });

import axios from 'axios';

const apiUrl = import.meta.env.VITE_API_URL;
const authStore = useAuthStore();

// Props
const { modalState } = defineProps({
    modalState: {
        type: Boolean,
        required: true,
    },
});

// Emits

const emit = defineEmits(['closeModal']);

// Computed
const canRequest = computed(() => {
    return intersectionUserData.value.skip + 5 <= searchResultCount.value;
});

const computedSearchValue = computed(() => {
    return searchValue.value;
});

// Local variables
const searchValue = ref('');
const dataLoading = ref(false);
const searchResultText = ref('');
const searchResultCount = ref(null);
const modalStateWatchValue = ref(modalState);
const searchedUsers = ref([]);
const recentSearched = ref(localStorage.getItem('recentSearched') ? JSON.parse(localStorage.getItem('recentSearched')) : []);
const userGetData = ref({
    search: computedSearchValue,
    extra: false,
    limit: 5,
    skip: 0,
});
const intersectionUserData = ref({
    search: computedSearchValue,
    extra: false,
    limit: 5,
    skip: 0,
});

const lastElement = ref(null);

// Methods

const emitCtrlK = (e) => {
    if (e.key == 'Escape') {
        emit('closeModal');
        return;
    }

    if (['k', 'K'].includes(e.key)) {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            emit('closeModal');
        }
    }
};

const clearSearchValue = () => {
    searchValue.value = '';
};

const recentUserClicked = (userID) => {
    console.log(userID, 'UserID');
};

const userClicked = (user) => {
    const recentSearchedUsers = localStorage.getItem('recentSearched');

    if (recentSearchedUsers) {
        const recentSearchedUsersArr = JSON.parse(recentSearchedUsers);

        const hasUser = recentSearchedUsersArr.find((recentUser) => recentUser.user === user.id);

        if (!hasUser) {
            recentSearchedUsersArr.unshift(user);

            recentSearched.value = [...recentSearchedUsersArr];
            localStorage.setItem('recentSearched', JSON.stringify(recentSearchedUsersArr));
        }

        return;
    }

    localStorage.setItem('recentSearched', JSON.stringify([user]));
};

const getSearchUsers = async () => {
    try {
        const {
            data: { data: usersData },
        } = await axios.get(`${apiUrl}/v1/examiners/search`, {
            params: userGetData.value,
            headers: {
                Authorization: `Bearer ${authStore.userToken}`,
                'Content-Type': 'application/json',
            },
        });

        return usersData;
    } catch ({ response }) {
        throw new Error(response.status);
    }
};

const intersectionGetSearchUsers = async () => {
    intersectionUserData.value.skip += 5;

    try {
        const {
            data: {
                data: { result },
            },
        } = await axios.get(`${apiUrl}/v1/examiners/search`, {
            params: intersectionUserData.value,
            headers: {
                Authorization: `Bearer ${authStore.userToken}`,
                'Content-Type': 'application/json',
            },
        });

        searchedUsers.value = [...searchedUsers.value, ...result];
    } catch ({ response }) {
        throw new Error(response.status);
    }
};

const intersectionObserverCallback = (entries) => {
    entries.forEach(async (entry) => {
        if (entry.intersectionRatio > 0) {
            if (canRequest) {
                await intersectionGetSearchUsers();
                io.unobserve(lastElement.value);
                lastElement.value = [...document.querySelectorAll('.searchedItem')].at(-1);
                io.observe(lastElement.value);
            }
        }
    });
};

const io = new IntersectionObserver(intersectionObserverCallback);

const focusCallback = async (event) => {
    if (canRequest) {
        await intersectionGetSearchUsers();
        io.unobserve(lastElement.value);
        lastElement.value = [...document.querySelectorAll('.searchedItem')].at(-1);
        io.observe(lastElement.value);
    }
};

const debouncedFn = useDebounceFn(async () => {
    if (searchValue.value) {
        dataLoading.value = true;
        try {
            const { result, total } = await getSearchUsers();

            searchResultCount.value = total;
            searchedUsers.value = [...result];

            dataLoading.value = false;
            if (result.length) {
                searchResultText.value = `${total} results`;

                if (total > 5) {
                    setTimeout(() => {
                        if (lastElement.value) {
                            io.unobserve(lastElement.value);
                            intersectionUserData.value = userGetData.value;
                        }

                        lastElement.value = [...document.querySelectorAll('.searchedItem')].at(-1);
                        lastElement.value.addEventListener('focus', focusCallback);
                        io.observe(lastElement.value);
                    });
                }

                return;
            }

            searchResultText.value = 'No results';
        } catch (error) {
            searchResultText.value = 'No results';
            dataLoading.value = false;
        }
    }
}, 1000);

// Watchers
watch(modalStateWatchValue, (newValue) => {
    if (newValue) {
        searchValue.value = '';
    }
});

watch(searchValue, (newValue) => {
    if (!newValue) {
        searchedUsers.value = [];
        searchResultText.value = '';
    }
});

onBeforeUnmount(() => {
    if (lastElement.value) {
        lastElement.value.removeEventListener('focus', focusCallback);
    }
});
</script>

<style scoped>
.search-content__wrapper {
    @apply select-none;
}

.search-content__wrapper__search-block {
    @apply pb-3 mb-4 border-b dark:border-gray-400;
}

:deep(.search-content__icon-field .p-inputtext) {
    @apply border-0 shadow-none w-full p-0 text-2xl;
}

.search-icon {
    font-size: 1.2rem;
}

.search-content__clear-btn {
    @apply cursor-pointer text-[#1c1c1c20] hover:text-[--main-black] dark:text-[--main-white] dark:hover:text-gray-400;
}

span:is(.search-results__title, .recent-searches__title) {
    @apply text-[14px] text-[--text-gray] dark:text-[--main-white];
}

div:is(.recent-searches, .search-results) {
    @apply flex flex-col gap-y-2;
}
</style>
